import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import KeyboardVoiceOutlinedIcon from '@mui/icons-material/KeyboardVoiceOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import ThumbUpOffAltOutlinedIcon from '@mui/icons-material/ThumbUpOffAltOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TextFieldsOutlinedIcon from '@mui/icons-material/TextFieldsOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import DatabaseOutlinedIcon from '@mui/icons-material/StorageOutlined';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import FunctionsOutlinedIcon from '@mui/icons-material/FunctionsOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PasswordOutlinedIcon from '@mui/icons-material/PasswordOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import IntegrationInstructionsOutlinedIcon from '@mui/icons-material/IntegrationInstructionsOutlined';

export const FLOW_NODE_CATEGORIES = [
  {
    key: 'prompts',
    label: 'Prompts',
    description: 'Collect user responses with fast, reusable prompt patterns.',
  },
  {
    key: 'message',
    label: 'Message',
    description: 'Send polished content blocks across web and WhatsApp.',
  },
  {
    key: 'actions',
    label: 'Actions',
    description: 'Run workflows, call APIs, and trigger backend operations.',
  },
  {
    key: 'logic',
    label: 'Logic',
    description: 'Control branching and route users down the right path.',
  },
  {
    key: 'integrations',
    label: 'Integrations',
    description: 'Connect the flow with payment and platform services.',
  },
];

/** Purpose: Construct a consistent node-library descriptor. Why: Canvas creation and properties panels depend on common catalog fields. */
const createItem = ({
  key,
  label,
  category,
  group = 'others',
  type,
  description,
  icon,
  accent,
  config = {},
}) => ({
  key,
  label,
  category,
  group,
  type,
  description,
  icon,
  accent,
  config,
});

const FLOW_NODE_LIBRARY_ALL = [
  createItem({
    key: 'question',
    label: 'Question',
    category: 'prompts',
    group: 'frequently_used',
    type: 'input',
    description: 'Ask a direct question and store the user reply.',
    icon: QuestionAnswerOutlinedIcon,
    accent: 'from-sky-500 to-cyan-400',
    config: { label: 'Question', prompt: 'What can I help you with?', variable: 'user_question', placeholder: 'Type here', required: true },
  }),
  createItem({
    key: 'quick_replies',
    label: 'Quick replies',
    category: 'prompts',
    group: 'frequently_used',
    type: 'input',
    description: 'Capture a guided response with ready-made choices.',
    icon: ChatBubbleOutlineIcon,
    accent: 'from-sky-500 to-indigo-400',
    config: { label: 'Quick replies', prompt: 'Choose an option below', variable: 'quick_reply', placeholder: 'Pick one', required: true, options: ['Option 1', 'Option 2', 'Option 3'] },
  }),
  createItem({
    key: 'name',
    label: 'Name',
    category: 'prompts',
    group: 'frequently_used',
    type: 'input',
    description: 'Collect and store the visitor name.',
    icon: BadgeOutlinedIcon,
    accent: 'from-emerald-500 to-teal-400',
    config: { label: 'Name', prompt: 'May I know your name?', variable: 'customer_name', placeholder: 'John Doe', required: true },
  }),
  createItem({
    key: 'email',
    label: 'Email',
    category: 'prompts',
    group: 'frequently_used',
    type: 'input',
    description: 'Collect and validate an email address.',
    icon: AlternateEmailIcon,
    accent: 'from-emerald-500 to-cyan-400',
    config: { label: 'Email', prompt: 'What is your email address?', variable: 'customer_email', placeholder: 'name@example.com', required: true, validation: 'email' },
  }),
  createItem({
    key: 'phone',
    label: 'Phone',
    category: 'prompts',
    group: 'frequently_used',
    type: 'input',
    description: 'Capture a phone number for follow-up.',
    icon: LocalPhoneOutlinedIcon,
    accent: 'from-amber-500 to-orange-400',
    config: { label: 'Phone', prompt: 'Share your phone number', variable: 'customer_phone', placeholder: '+91 98765 43210', required: true, validation: 'phone' },
  }),
  createItem({
    key: 'carousel_prompt',
    label: 'Carousel',
    category: 'prompts',
    group: 'frequently_used',
    type: 'message',
    description: 'Guide a choice with swipeable card options.',
    icon: ViewCarouselOutlinedIcon,
    accent: 'from-fuchsia-500 to-violet-400',
    config: { label: 'Carousel', message: 'Show carousel cards here.' },
  }),
  createItem({
    key: 'voice_input',
    label: 'Voice input',
    category: 'prompts',
    group: 'frequently_used',
    type: 'input',
    description: 'Collect spoken input for voice-enabled journeys.',
    icon: KeyboardVoiceOutlinedIcon,
    accent: 'from-rose-500 to-pink-400',
    config: { label: 'Voice input', prompt: 'Please say your response after the tone.', variable: 'voice_input', placeholder: 'Voice transcript', required: true },
  }),
  createItem({
    key: 'dynamic_chat',
    label: 'Dynamic chat',
    category: 'prompts',
    group: 'frequently_used',
    type: 'message',
    description: 'Run a goal-driven conversation, collect fields and call approved workflows.',
    icon: AutoAwesomeOutlinedIcon,
    accent: 'from-violet-500 to-indigo-400',
    config: {
      label: 'Dynamic chat',
      conversationMode: true,
      maxTurns: 40,
      collectFields: [],
      workflowSkills: [],
      prompt: 'Reply helpfully to the user using the current conversation context.',
      systemPrompt: 'You are a helpful customer support assistant. Follow the configured business rules.',
      responseVariable: 'journey.ai_reply',
      failureMessage: 'I could not generate a response right now. Please try again.',
      temperature: 0.4,
      maxTokens: 1200,
      allowedSkills: [],
    },
  }),
  createItem({
    key: 'date',
    label: 'Date',
    category: 'prompts',
    type: 'input',
    description: 'Ask the user for a date.',
    icon: CalendarMonthOutlinedIcon,
    accent: 'from-teal-500 to-cyan-400',
    config: {
      label: 'Date',
      prompt: 'Select a date',
      variable: 'selected_date',
      placeholder: 'DD/MM/YYYY',
      required: true,
      validation: 'date',
      dateValidation: {
        allowToday: true,
        allowPast: true,
        allowFuture: true,
        maxPastDays: null,
        maxFutureDays: null,
      },
    },
  }),
  createItem({
    key: 'store_comment',
    label: 'Store Comment',
    category: 'prompts',
    type: 'input',
    description: 'Capture a free-form comment from the user.',
    icon: CommentOutlinedIcon,
    accent: 'from-slate-500 to-slate-400',
    config: { label: 'Store Comment', prompt: 'Leave a note for our team', variable: 'comment', placeholder: 'Type your comment', required: false },
  }),
  createItem({
    key: 'whatsapp_list_prompt',
    label: 'WhatsApp List',
    category: 'prompts',
    type: 'message',
    description: 'Offer WhatsApp list-style choices.',
    icon: ListAltOutlinedIcon,
    accent: 'from-emerald-500 to-lime-400',
    config: {
      label: 'WhatsApp List',
      title: 'Choose an option',
      prompt: 'Choose an option',
      variable: 'selected_list_item',
      required: true,
      listItems: [
        { label: 'Option 1', value: 'option_1', description: 'First choice' },
        { label: 'Option 2', value: 'option_2', description: 'Second choice' },
      ],
    },
  }),
  createItem({
    key: 'file_prompt',
    label: 'File Prompt',
    category: 'prompts',
    type: 'input',
    description: 'Ask the user to upload a file.',
    icon: AttachFileOutlinedIcon,
    accent: 'from-cyan-500 to-blue-400',
    config: { label: 'File Prompt', prompt: 'Please upload your file', variable: 'uploaded_file', placeholder: 'Awaiting file upload', required: true },
  }),
  createItem({
    key: 'multi_select',
    label: 'Multi Select',
    category: 'prompts',
    type: 'input',
    description: 'Let the user choose multiple options.',
    icon: CheckBoxOutlinedIcon,
    accent: 'from-orange-500 to-amber-400',
    config: { label: 'Multi Select', prompt: 'Select all that apply', variable: 'selected_options', placeholder: 'Multiple values', required: false },
  }),
  createItem({
    key: 'feedback',
    label: 'Feedback',
    category: 'prompts',
    type: 'input',
    description: 'Capture sentiment or satisfaction feedback.',
    icon: ThumbUpOffAltOutlinedIcon,
    accent: 'from-lime-500 to-emerald-400',
    config: { label: 'Feedback', prompt: 'How was your experience today?', variable: 'feedback_score', placeholder: 'Your feedback', required: false },
  }),
  createItem({
    key: 'location',
    label: 'Location',
    category: 'prompts',
    type: 'input',
    description: 'Collect a city, address, or live location.',
    icon: LocationOnOutlinedIcon,
    accent: 'from-rose-500 to-orange-400',
    config: { label: 'Location', prompt: 'Please share your location', variable: 'user_location', placeholder: 'City or address', required: true },
  }),
  createItem({
    key: 'speak',
    label: 'Speak',
    category: 'prompts',
    type: 'message',
    description: 'Speak a message aloud in voice flows.',
    icon: RecordVoiceOverOutlinedIcon,
    accent: 'from-violet-500 to-fuchsia-400',
    config: { label: 'Speak', message: 'Read this message aloud to the caller.' },
  }),
  createItem({
    key: 'input',
    label: 'Input',
    category: 'prompts',
    type: 'input',
    description: 'Capture any custom response.',
    icon: TextFieldsOutlinedIcon,
    accent: 'from-amber-500 to-yellow-400',
    config: { label: 'Input', prompt: 'Enter your response', variable: 'user_input', placeholder: 'Type here', required: true },
  }),
  createItem({
    key: 'product_search',
    label: 'Product search',
    category: 'prompts',
    type: 'input',
    description: 'Collect a product query before searching.',
    icon: SearchOutlinedIcon,
    accent: 'from-sky-500 to-blue-500',
    config: { label: 'Product search', prompt: 'What product are you looking for?', variable: 'product_query', placeholder: 'Search term', required: true },
  }),
  createItem({
    key: 'text',
    label: 'Text',
    category: 'message',
    group: 'frequently_used',
    type: 'message',
    description: 'Send a plain text response.',
    icon: TextFieldsOutlinedIcon,
    accent: 'from-sky-500 to-cyan-400',
    config: { label: 'Text message', message: 'Type your message here.' },
  }),
  createItem({
    key: 'image',
    label: 'Image',
    category: 'message',
    type: 'message',
    description: 'Send an image with caption.',
    icon: ImageOutlinedIcon,
    accent: 'from-fuchsia-500 to-violet-400',
    config: { label: 'Image', message: 'Send image with caption.' },
  }),
  createItem({
    key: 'message_carousel',
    label: 'Carousel',
    category: 'message',
    type: 'message',
    description: 'Present rich cards or product tiles.',
    icon: ViewCarouselOutlinedIcon,
    accent: 'from-indigo-500 to-sky-400',
    config: { label: 'Carousel', message: 'Add carousel cards here.' },
  }),
  createItem({
    key: 'video',
    label: 'Video',
    category: 'message',
    type: 'message',
    description: 'Send a video response.',
    icon: VideocamOutlinedIcon,
    accent: 'from-rose-500 to-fuchsia-400',
    config: { label: 'Video', message: 'Send a video message.' },
  }),
  createItem({
    key: 'file',
    label: 'File',
    category: 'message',
    type: 'message',
    description: 'Send a downloadable file.',
    icon: AttachFileOutlinedIcon,
    accent: 'from-cyan-500 to-teal-400',
    config: { label: 'File', message: 'Attach a file for the user.' },
  }),
  createItem({
    key: 'message_quick_replies',
    label: 'Quick replies',
    category: 'message',
    type: 'message',
    description: 'Display reply buttons with a message.',
    icon: ChatBubbleOutlineIcon,
    accent: 'from-sky-500 to-indigo-500',
    config: { label: 'Quick replies', prompt: 'Choose one of the quick replies below.', variable: 'quick_reply', required: true, options: ['Option 1', 'Option 2', 'Option 3'] },
  }),
  createItem({
    key: 'message_whatsapp_list',
    label: 'WhatsApp List',
    category: 'message',
    type: 'message',
    description: 'Send a WhatsApp-compatible list message.',
    icon: ListAltOutlinedIcon,
    accent: 'from-emerald-500 to-green-400',
    config: {
      label: 'WhatsApp List',
      title: 'Choose an option',
      prompt: 'Choose an option',
      variable: 'selected_list_item',
      required: true,
      listItems: [
        { label: 'Option 1', value: 'option_1', description: 'First choice' },
        { label: 'Option 2', value: 'option_2', description: 'Second choice' },
      ],
    },
  }),
  createItem({
    key: 'product_catalogue',
    label: 'WhatsApp product catalogue',
    category: 'message',
    type: 'message',
    description: 'Show catalogue products in WhatsApp.',
    icon: Inventory2OutlinedIcon,
    accent: 'from-orange-500 to-red-400',
    config: { label: 'Product catalogue', message: 'Show product catalogue here.' },
  }),
  createItem({
    key: 'variables',
    label: 'Variables',
    category: 'actions',
    group: 'frequently_used',
    type: 'api_call',
    description: 'Set or update variables for the journey.',
    icon: CategoryOutlinedIcon,
    accent: 'from-violet-500 to-indigo-500',
    config: { label: 'Variables', variableAssignments: [{ targetVariable: 'customer_name', value: '{{user_input}}', valueType: 'text' }], responseMessage: '' },
  }),
  createItem({
    key: 'database',
    label: 'Database',
    category: 'actions',
    group: 'frequently_used',
    type: 'api_call',
    description: 'Read or write data from your database layer.',
    icon: DatabaseOutlinedIcon,
    accent: 'from-cyan-500 to-blue-500',
    config: {
      label: 'Database',
      customStorage: {
        enabled: true,
        operation: 'create',
        tableId: '',
        limit: 25,
        failOnEmptyResult: true,
        filters: {},
        filterRules: [],
        payload: {},
        responseVariable: 'database_result',
      },
      responseMessage: '',
    },
  }),
  createItem({
    key: 'api',
    label: 'API',
    category: 'actions',
    group: 'frequently_used',
    type: 'api_call',
    description: 'Make an API call and store the response.',
    icon: ApiOutlinedIcon,
    accent: 'from-cyan-500 to-teal-400',
    config: { label: 'API', asyncRequest: { method: 'POST', url: '', headers: {}, body: {}, timeout: 10000, responseVariable: 'api_result' }, responseMessage: '' },
  }),
  createItem({
    key: 'function',
    label: 'Function',
    category: 'actions',
    group: 'frequently_used',
    type: 'api_call',
    description: 'Run a custom function or server action.',
    icon: FunctionsOutlinedIcon,
    accent: 'from-amber-500 to-orange-400',
    config: { label: 'Function', code: 'return { success: true };', outputVariable: 'function_result', timeout: 5000, responseMapping: [] },
  }),
  createItem({
    key: 'execute_flow',
    label: 'Execute Flow',
    category: 'actions',
    group: 'frequently_used',
    type: 'message',
    description: 'Jump into another flow or sub-flow.',
    icon: AccountTreeOutlinedIcon,
    accent: 'from-indigo-500 to-violet-400',
    config: { label: 'Execute Flow', message: 'Continue into another flow.', targetFlowId: '', returnAfterJump: false },
  }),
  createItem({
    key: 'raise_ticket',
    label: 'Raise Ticket',
    category: 'actions',
    group: 'frequently_used',
    type: 'message',
    description: 'Escalate the conversation into a support ticket.',
    icon: ConfirmationNumberOutlinedIcon,
    accent: 'from-rose-500 to-orange-400',
    config: {
      label: 'Raise Ticket',
      provider: 'native',
      name: '{{user.name}}',
      query: '{{variables.userQuery}}',
      mobile: '{{user.mobile}}',
      email: '{{user.email}}',
      queueId: '',
      skills: [],
      language: '',
      message: 'A support ticket has been created. Connecting you to support now.',
      assignmentMode: 'auto',
      noAgentMessage: 'All agents are busy right now. Your ticket has been queued and the team will respond soon.',
      priority: 'normal',
      queueName: '',
      department: '',
      groupName: '',
      routingTags: ['ticket'],
    },
  }),
  createItem({ key: 'send_otp', label: 'Send OTP', category: 'actions', type: 'api_call', description: 'Send a verification OTP.', icon: PasswordOutlinedIcon, accent: 'from-fuchsia-500 to-violet-400', config: { label: 'Send OTP', asyncRequest: { method: 'POST', url: '', headers: {}, body: {}, timeout: 10000, responseVariable: 'otp_sent' }, responseMessage: '' } }),
  createItem({ key: 'verify_otp', label: 'Verify OTP', category: 'actions', type: 'condition', description: 'Validate the OTP response.', icon: PasswordOutlinedIcon, accent: 'from-sky-500 to-indigo-500', config: { label: 'Verify OTP', variable: 'otp_status', operator: 'equals', value: 'verified' } }),
  createItem({ key: 'outbound_notification', label: 'Outbound Notification', category: 'actions', type: 'message', description: 'Trigger outbound message delivery.', icon: NotificationsActiveOutlinedIcon, accent: 'from-orange-500 to-amber-400', config: { label: 'Outbound Notification', message: 'Send outbound notification.' } }),
  createItem({ key: 'notification_status', label: 'Notification Status', category: 'actions', type: 'api_call', description: 'Check delivery status of a notification.', icon: NotificationsActiveOutlinedIcon, accent: 'from-slate-500 to-slate-400', config: { label: 'Notification Status', asyncRequest: { method: 'GET', url: '', headers: {}, body: {}, timeout: 10000, responseVariable: 'notification_status' }, responseMessage: '' } }),
  createItem({ key: 'data_formatter', label: 'Data formatter', category: 'actions', type: 'api_call', description: 'Transform payloads before sending.', icon: EditNoteOutlinedIcon, accent: 'from-cyan-500 to-sky-400', config: { label: 'Data formatter', asyncRequest: { method: 'POST', url: '', headers: {}, body: {}, timeout: 10000, responseVariable: 'formatted_data' }, responseMessage: '' } }),
  createItem({ key: 'set_language', label: 'Change Language', category: 'actions', group: 'frequently_used', type: 'message', description: 'Remember the conversation language for every next step.', icon: TranslateOutlinedIcon, accent: 'from-emerald-500 to-teal-400', config: { label: 'Change Language', languageCode: 'hi', message: 'Language changed to Hindi.', translations: { hi: { message: 'भाषा हिंदी में बदल दी गई है।' } } } }),
  createItem({ key: 'detect_language', label: 'Auto Detect Language', category: 'actions', group: 'frequently_used', type: 'message', description: 'Auto-detect Indian scripts (Hindi, Marathi, Gujarati, etc.) & Hinglish and switch language.', icon: TranslateOutlinedIcon, accent: 'from-teal-500 to-indigo-500', config: { label: 'Auto Detect Language', saveToVariable: 'detected_language', fallbackLanguage: 'en', updateSessionLanguage: true, detectSource: 'last_inbound', sourceVariable: '' } }),
  createItem({ key: 'voice_call', label: 'Voice call', category: 'actions', type: 'message', description: 'Trigger a voice call action.', icon: KeyboardVoiceOutlinedIcon, accent: 'from-violet-500 to-fuchsia-500', config: { label: 'Voice call', message: 'Initiate voice call handoff.' } }),
  createItem({ key: 'delay', label: 'Delay', category: 'actions', type: 'message', description: 'Pause before continuing.', icon: AccessTimeOutlinedIcon, accent: 'from-amber-500 to-yellow-400', config: { label: 'Delay', message: 'Pause flow before continuing.' } }),
  createItem({ key: 'modifier', label: 'Modifier', category: 'actions', type: 'api_call', description: 'Modify variables or payload data.', icon: EditNoteOutlinedIcon, accent: 'from-slate-500 to-blue-400', config: { label: 'Modifier', asyncRequest: { method: 'POST', url: '', headers: {}, body: {}, timeout: 10000, responseVariable: 'modified_data' }, responseMessage: '' } }),
  createItem({ key: 'switch_flow', label: 'Switch flow', category: 'actions', type: 'message', description: 'Switch the user into a different journey.', icon: HubOutlinedIcon, accent: 'from-indigo-500 to-sky-500', config: { label: 'Switch flow', message: 'Switch to another flow.' } }),
  createItem({ key: 'generate_pdf_image', label: 'Generate PDF/Image', category: 'actions', type: 'api_call', description: 'Generate a document or asset from collected data.', icon: PictureAsPdfOutlinedIcon, accent: 'from-rose-500 to-red-400', config: { label: 'Generate PDF/Image', asyncRequest: { method: 'POST', url: '', headers: {}, body: {}, timeout: 10000, responseVariable: 'generated_asset' }, responseMessage: '' } }),
  createItem({ key: 'analytics', label: 'Analytics', category: 'actions', type: 'api_call', description: 'Track a reporting or analytics event.', icon: InsightsOutlinedIcon, accent: 'from-sky-500 to-cyan-400', config: { label: 'Analytics', asyncRequest: { method: 'POST', url: '', headers: {}, body: {}, timeout: 10000, responseVariable: 'analytics_response' }, responseMessage: '' } }),
  createItem({ key: 'send_event', label: 'Send event', category: 'actions', type: 'api_call', description: 'Dispatch an event into another system.', icon: EventNoteOutlinedIcon, accent: 'from-violet-500 to-indigo-400', config: { label: 'Send event', asyncRequest: { method: 'POST', url: '', headers: {}, body: {}, timeout: 10000, responseVariable: 'event_status' }, responseMessage: '' } }),
  createItem({ key: 'knowledge_search', label: 'Knowledge Search', category: 'actions', type: 'api_call', description: 'Search docs or knowledge base content.', icon: MenuBookOutlinedIcon, accent: 'from-emerald-500 to-cyan-400', config: { label: 'Knowledge Search', asyncRequest: { method: 'POST', url: '', headers: {}, body: {}, timeout: 10000, responseVariable: 'knowledge_result' }, responseMessage: '' } }),
  createItem({ key: 'payment', label: 'Payment', category: 'actions', type: 'message', description: 'Share a payment step with the user.', icon: PaymentsOutlinedIcon, accent: 'from-amber-500 to-orange-500', config: { label: 'Payment', message: 'Proceed to payment.' } }),
  createItem({ key: 'connect_transxt', label: 'Connect TransXT', category: 'actions', type: 'api_call', description: 'Connect to TransXT workflow.', icon: HubOutlinedIcon, accent: 'from-indigo-500 to-violet-400', config: { label: 'Connect TransXT', asyncRequest: { method: 'POST', url: '', headers: {}, body: {}, timeout: 10000, responseVariable: 'transxt_result' }, responseMessage: '' } }),
  createItem({ key: 'user_event', label: 'User event', category: 'actions', type: 'api_call', description: 'Track or emit user lifecycle events.', icon: PersonAddAltOutlinedIcon, accent: 'from-emerald-500 to-lime-400', config: { label: 'User event', asyncRequest: { method: 'POST', url: '', headers: {}, body: {}, timeout: 10000, responseVariable: 'user_event_status' }, responseMessage: '' } }),
  createItem({
    key: 'chat_handoff',
    label: 'Chat handoff',
    category: 'actions',
    type: 'message',
    description: 'Hand the conversation over to a live agent.',
    icon: PersonAddAltOutlinedIcon,
    accent: 'from-rose-500 to-fuchsia-400',
    config: {
      label: 'Chat handoff',
      provider: 'native',
      name: '{{user.name}}',
      query: '{{variables.userQuery}}',
      mobile: '{{user.mobile}}',
      email: '{{user.email}}',
      queueId: '',
      skills: [],
      language: '',
      message: 'Connecting you to a live agent now.',
      assignmentMode: 'auto',
      noAgentMessage: 'No agent is available right now. Your chat has been queued and someone will join shortly.',
      assignedMessage: 'Your chat has been assigned to {{assigned_agent_name}}. They will assist you now.',
      resumeOnResolve: true,
      resolvedMessage: 'Your support conversation has been resolved.',
      queueWaitSeconds: '',
      priority: 'normal',
      queueName: '',
      department: '',
      groupName: '',
      routingTags: ['live_chat'],
    },
  }),
  createItem({ key: 'name_validator', label: 'Name validator', category: 'actions', group: 'validators', type: 'condition', description: 'Validate a collected name.', icon: RuleOutlinedIcon, accent: 'from-teal-500 to-emerald-400', config: { label: 'Name validator', variable: 'customer_name', operator: 'exists', value: '' } }),
  createItem({ key: 'email_validator', label: 'Email validator', category: 'actions', group: 'validators', type: 'condition', description: 'Validate an email address.', icon: RuleOutlinedIcon, accent: 'from-cyan-500 to-sky-400', config: { label: 'Email validator', variable: 'customer_email', operator: 'contains', value: '@' } }),
  createItem({ key: 'phone_validator', label: 'Phone validator', category: 'actions', group: 'validators', type: 'condition', description: 'Validate a phone number.', icon: RuleOutlinedIcon, accent: 'from-amber-500 to-orange-400', config: { label: 'Phone validator', variable: 'customer_phone', operator: 'exists', value: '' } }),
  createItem({ key: 'date_time_validator', label: 'Date & time validator', category: 'actions', group: 'validators', type: 'condition', description: 'Validate date or time input.', icon: RuleOutlinedIcon, accent: 'from-violet-500 to-indigo-400', config: { label: 'Date & time validator', variable: 'selected_date', operator: 'exists', value: '' } }),
  createItem({ key: 'location_validator', label: 'Location validator', category: 'actions', group: 'validators', type: 'condition', description: 'Validate location input.', icon: RuleOutlinedIcon, accent: 'from-rose-500 to-red-400', config: { label: 'Location validator', variable: 'user_location', operator: 'exists', value: '' } }),
  createItem({ key: 'file_validator', label: 'File validator', category: 'actions', group: 'validators', type: 'condition', description: 'Validate uploaded file metadata.', icon: RuleOutlinedIcon, accent: 'from-slate-500 to-blue-400', config: { label: 'File validator', variable: 'uploaded_file', operator: 'exists', value: '' } }),
  createItem({ key: 'condition', label: 'Condition (if-else)', category: 'logic', type: 'condition', description: 'Create a yes/no branch with rule-based logic.', icon: RuleOutlinedIcon, accent: 'from-violet-500 to-fuchsia-400', config: { label: 'Condition', variable: 'customer_status', operator: 'equals', value: 'active' } }),
  createItem({ key: 'channel_filter', label: 'Channel Filter', category: 'logic', type: 'condition', description: 'Branch based on channel like web or WhatsApp.', icon: FilterAltOutlinedIcon, accent: 'from-sky-500 to-blue-500', config: { label: 'Channel Filter', variable: 'channel', operator: 'equals', value: 'web' } }),
  createItem({ key: 'ab_split', label: 'A/B Split Test', category: 'logic', group: 'frequently_used', type: 'condition', description: 'Split traffic between Path A and Path B with configurable ratio (e.g. 50/50).', icon: AccountTreeOutlinedIcon, accent: 'from-purple-500 to-pink-500', config: { label: 'A/B Split Test', ratioA: 50, saveToVariable: 'ab_variant' } }),
  createItem({ key: 'razorpay', label: 'Razorpay', category: 'integrations', type: 'message', description: 'Share a payment link or payment step via Razorpay.', icon: IntegrationInstructionsOutlinedIcon, accent: 'from-indigo-500 to-cyan-400', config: { label: 'Razorpay', message: 'Share Razorpay payment link.' } }),
];

const SUPPORTED_NODE_KEYS = new Set([
  'question',
  'quick_replies',
  'name',
  'email',
  'phone',
  'voice_input',
  'dynamic_chat',
  'date',
  'store_comment',
  'whatsapp_list_prompt',
  'feedback',
  'location',
  'input',
  'product_search',
  'text',
  'image',
  'video',
  'file',
  'message_quick_replies',
  'message_whatsapp_list',
  'variables',
  'database',
  'api',
  'function',
  'execute_flow',
  'switch_flow',
  'raise_ticket',
  'chat_handoff',
  'set_language',
  'detect_language',
  'send_otp',
  'notification_status',
  'data_formatter',
  'modifier',
  'analytics',
  'knowledge_search',
  'user_event',
  'condition',
  'channel_filter',
  'ab_split',
  'verify_otp',
  'name_validator',
  'email_validator',
  'phone_validator',
  'date_time_validator',
  'location_validator',
]);

/** Purpose: Expose supported node keys for authoring. Why: Legacy entries remain loadable without being offered for new flows. */
export const FLOW_NODE_LIBRARY = FLOW_NODE_LIBRARY_ALL.filter((item) => SUPPORTED_NODE_KEYS.has(item.key));

export const GROUP_LABELS = {
  frequently_used: 'Frequently used',
  validators: 'Validators',
  others: 'Others',
};

/** Purpose: Resolve a known definition by node key. Why: Saved legacy nodes still require editor metadata. */
export const findFlowNodeItem = (key) => FLOW_NODE_LIBRARY_ALL.find((item) => item.key === key) || null;

/** Purpose: Resolve category metadata with a fallback. Why: Unknown category keys must not break catalog presentation. */
export const getFlowNodeCategory = (categoryKey) =>
  FLOW_NODE_CATEGORIES.find((category) => category.key === categoryKey) || FLOW_NODE_CATEGORIES[0];
